import { useState } from 'react';
import { Alert, Box, Button, ColumnLayout, FormField, Header, Input, Modal, SpaceBetween, Textarea } from '@amzn/awsui-components-react';
import type { Topic } from '../../../client/IvtHubClient';
import type { UpsertTopicInput } from '../../../client/topicClient';

interface TopicMetadataModalProps {
  /** Pass a Topic to edit / view it; omit to switch into "create new" mode. */
  topic?: Topic;
  editable: boolean;
  saving: boolean;
  onClose: () => void;
  /** Resolves `null` on success (the host can close the modal then), or a
   *  ready-to-show failure message. `void` is accepted for view-only hosts. */
  onSave: (input: UpsertTopicInput) => Promise<string | null> | Promise<void> | void;
}

export default function TopicMetadataModal({
  topic,
  editable,
  saving,
  onClose,
  onSave,
}: TopicMetadataModalProps) {
  const isCreate = !topic;

  const [name, setName] = useState(topic?.name ?? '');
  const [description, setDescription] = useState(topic?.description ?? '');
  const [sharedTech, setSharedTech] = useState(topic?.sharedTech ?? '');
  const [knowledgeBase, setKnowledgeBase] = useState(topic?.knowledgeBase ?? '');
  const [potential, setPotential] = useState(topic?.potential ?? '');
  const [agentName, setAgentName] = useState(topic?.agent?.name ?? '');
  const [agentCapabilities, setAgentCapabilities] = useState(topic?.agent?.capabilities ?? '');
  const [agentDataSources, setAgentDataSources] = useState(topic?.agent?.dataSources ?? '');
  const [agentMcpTools, setAgentMcpTools] = useState(topic?.agent?.mcpTools ?? '');
  const [error, setError] = useState<string | null>(null);

  const formEditable = isCreate || editable;
  const disabled = !formEditable || saving;

  const handleSave = async () => {
    setError(null);
    const finalName = isCreate ? name.trim() : topic?.name;
    if (!finalName) {
      setError('Topic 名称不能为空');
      return;
    }
    const result = await onSave({
      topicName: finalName,
      description,
      sharedTech,
      knowledgeBase,
      potential,
      agent: {
        name: agentName,
        capabilities: agentCapabilities,
        dataSources: agentDataSources,
        mcpTools: agentMcpTools,
      },
    });
    if (typeof result === 'string') setError(result);
  };

  const title = isCreate ? '新建 Topic' : topic?.name;
  const sub = isCreate
    ? '跨行业能力 / 技术主题（除 Name 外都可选）'
    : editable
      ? '编辑 Topic 元数据'
      : '查看 Topic 元数据（进入编辑模式以修改）';

  return (
    <Modal
      visible
      onDismiss={() => {
        if (!saving) onClose();
      }}
      header={title}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={saving}>
              {formEditable ? 'Cancel' : 'Close'}
            </Button>
            {formEditable && (
              <Button variant="primary" onClick={handleSave} loading={saving} disabled={isCreate && !name.trim()}>
                Save
              </Button>
            )}
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="small">{sub}</Box>

        {isCreate && (
          <FormField label="Name *">
            <Input
              value={name}
              onChange={({ detail }) => setName(detail.value)}
              disabled={disabled}
              placeholder="e.g. GenAI Agent 框架"
              autoFocus
            />
          </FormField>
        )}

        <FormField label="Description">
          <Textarea
            value={description}
            onChange={({ detail }) => setDescription(detail.value)}
            disabled={disabled}
            placeholder="Topic 简介"
          />
        </FormField>

        <FormField label="Shared Tech Stack">
          <Input
            value={sharedTech}
            onChange={({ detail }) => setSharedTech(detail.value)}
            disabled={disabled}
          />
        </FormField>

        <FormField label="Knowledge Base">
          <Input
            value={knowledgeBase}
            onChange={({ detail }) => setKnowledgeBase(detail.value)}
            disabled={disabled}
            placeholder="URL 或路径"
          />
        </FormField>

        <FormField label="Potential（潜在方向）">
          <Input
            value={potential}
            onChange={({ detail }) => setPotential(detail.value)}
            disabled={disabled}
          />
        </FormField>

        <Header variant="h3">Agent</Header>

        <ColumnLayout columns={2}>
          <FormField label="Name">
            <Input
              value={agentName}
              onChange={({ detail }) => setAgentName(detail.value)}
              disabled={disabled}
            />
          </FormField>
          <FormField label="Capabilities">
            <Input
              value={agentCapabilities}
              onChange={({ detail }) => setAgentCapabilities(detail.value)}
              disabled={disabled}
            />
          </FormField>
          <FormField label="Data Sources">
            <Input
              value={agentDataSources}
              onChange={({ detail }) => setAgentDataSources(detail.value)}
              disabled={disabled}
            />
          </FormField>
          <FormField label="MCP Tools">
            <Input
              value={agentMcpTools}
              onChange={({ detail }) => setAgentMcpTools(detail.value)}
              disabled={disabled}
            />
          </FormField>
        </ColumnLayout>

        {error && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
